import api, { apiLongOperation } from '../utils/api';

export interface TagProcessingLog {
  id?: string;
  timestamp: string;
  level: 'info' | 'success' | 'error' | string;
  message: string;
  tomaNumber?: number;
  tags?: number;
  inserted?: number;
  updated?: number;
  error?: string;
}

export interface TagProcessingHistoryItem {
  tomaNumber: number;
  tableName?: string | null;
  processedAt: string;
  rowsScanned: number;
  rowsWithTags: number;
  tagsExtracted: number;
  inserted: number;
  updated: number;
  durationMs?: number;
  status: 'completed' | 'error';
  errorMessage?: string | null;
}

export interface TagProcessingAvailableToma {
  tomaNumber: number;
  tableName?: string | null;
  fecha?: string | null;
  tagsProcessed: boolean;
  status: 'pending' | 'processing' | 'completed';
}

export interface TagSelectionInfo {
  tagDisplay?: string;
  updatedAt?: string;
  [key: string]: unknown;
}

export interface TagSelectionInput {
  tagKey: string;
  tagDisplay?: string;
}

export interface TagPreviewTotals {
  tomaCount: number;
  rowsScanned: number;
  rowsWithTags: number;
  uniqueTags: number;
  tagPairs: number;
  selectedTags: number;
}

export interface TagPreviewTag {
  key: string;
  occurrences: number;
  rowsWithTag: number;
  tomaCount: number;
  sampleValues: string[];
  distinctValues?: number;
}

export interface TagPreviewTomaTag {
  key: string;
  occurrences: number;
  rowsWithTag: number;
  sampleValues: string[];
  distinctValues?: number;
}

export interface TagPreviewToma {
  tomaNumber: number;
  tableName?: string | null;
  rowsScanned: number;
  rowsWithTags: number;
  totalTagPairs: number;
  tagsCount: number;
  tags: TagPreviewTomaTag[];
}

export interface TagPreviewResponse {
  success: boolean;
  clientId: string;
  totals: TagPreviewTotals;
  tags: TagPreviewTag[];
  tomas: TagPreviewToma[];
  selectedTags?: Record<string, TagSelectionInfo | string> | null;
  hasSelection?: boolean;
}

export interface TagProcessingCurrentJob {
  jobId: string;
  status: 'in_progress' | 'completed' | 'error';
  startedAt: string;
  lastUpdatedAt: string;
  processedTomasCount: number;
  totalTomas: number;
  progressPercentage: number;
  currentToma: number | null;
  logs: TagProcessingLog[];
  mode?: 'full_resync' | 'incremental' | string;
  selectedTagsCount?: number;
}

export interface TagProcessingTotals {
  inserted: number;
  updated: number;
  tagsExtracted: number;
}

export interface TagProcessingStatusResponse {
  success: boolean;
  status: 'idle' | 'in_progress' | 'completed' | 'error' | string;
  isProcessing?: boolean;
  lastProcessedToma: number;
  lastProcessedAt?: string | null;
  lastUpdatedAt?: string | null;
  totals: TagProcessingTotals;
  history: TagProcessingHistoryItem[];
  currentJob: TagProcessingCurrentJob | null;
  availableTomas: TagProcessingAvailableToma[];
  pendingCount: number;
  currentTomaNumber?: number | null;
  selectedTags?: Record<string, TagSelectionInfo | string> | null;
  errorMessage?: string | null;
  message?: string;
  needsFullResync?: boolean;
}

export async function startTagProcessing(
  clientId: string,
  options: { force?: boolean; tagsSelection?: TagSelectionInput[]; specificTomas?: number[] } = {}
) {
  const payload: Record<string, unknown> = {};

  if (options.force) {
    payload.force = true;
  }

  if (Array.isArray(options.tagsSelection)) {
    payload.tagsSelection = options.tagsSelection;
  }

  if (Array.isArray(options.specificTomas) && options.specificTomas.length > 0) {
    payload.specificTomas = options.specificTomas;
  }

  const response = await apiLongOperation.post(`/api/clients/${clientId}/tags/process`, payload);
  return response.data as {
    success: boolean;
    started: boolean;
    jobId?: string;
    totalTomas?: number;
    pendingTomas?: number[];
    message?: string;
    lastProcessedToma?: number;
    needsFullResync?: boolean;
  };
}

export async function getTagProcessingStatus(clientId: string) {
  const response = await api.get(`/api/clients/${clientId}/tags/status`);
  return response.data as TagProcessingStatusResponse;
}

export async function getTagPreview(clientId: string, tomaNumber?: number) {
  const params = tomaNumber ? { tomaNumber } : {};
  const response = await api.get(`/api/clients/${clientId}/tags/preview`, { params });
  return response.data as TagPreviewResponse;
}
